<template>
    <div class="text-slate-800 h-full flex flex-col overflow-hidden">
        <div
            class="bg-white p-6 flex flex-col gap-4 border-b border-x-0 border-t-0 border-b-slate-100 border-solid shrink-0">

            <div>
                <div class="text-2xl font-extrabold">
                    <slot name="header">Select Items</slot>
                </div>
                <div class="text-sm text-slate-500">
                    <slot name="subheader">You can select multiple items.</slot>
                </div>
            </div>

            <div>
                <vx-tabs v-model="tab"
                    size="lg">
                    <vx-tab value="all">All</vx-tab>
                    <vx-tab value="selected">Selected ({{ selected.length }})</vx-tab>
                </vx-tabs>
            </div>
            <div>
                <vx-input ref="searchEl"
                    v-model="search"
                    @keydown.enter="handleEnter"
                    size="lg"
                    data-test="search-input">
                    <template v-slot:right>
                        <button
                            v-if="hasSearchText"
                            @click="clearSearch"
                            type="button"
                            aria-label="Clear search"
                            class="cursor-pointer"
                            data-test="clear-search-button"
                        >
                            <font-awesome-icon widthAuto :icon="faXmark"></font-awesome-icon>
                        </button>
                        <font-awesome-icon v-else widthAuto :icon="faMagnifyingGlass"></font-awesome-icon>
                    </template>
                </vx-input>
            </div>
        </div>

        <div class="flex-1 min-h-0 flex flex-col">

            <slot name="before-items"/>

            <div v-if="tab === 'all'" class="flex-1 min-h-0 flex flex-col">

                <template v-if="filteredOptions.length > 0">
                    <div
                        v-bind="allContainerProps"
                        class="flex-1 min-h-0 overflow-y-auto pt-6 px-6 lg:pt-10 lg:pb-8 lg:px-12">
                        <div v-if="props.showSelectAll && filteredOptions.length > 0"
                            class="flex justify-between items-center pl-3 sm:pl-4 pb-2">
                            <div @click="toggleAll()" role="button" tabindex="0" data-test="select-all-button"
                                @keydown.space.prevent="toggleAll()" @keydown.enter.prevent="toggleAll()"
                                class="flex items-center gap-2 cursor-pointer ml-px">
                                <vx-checkbox :modelValue="selectAll" :tabindex="-1"></vx-checkbox>
                                <span class="text-sm whitespace-nowrap">Select All</span>
                            </div>
                            <slot name="select-all-right"/>
                        </div>
                        <div v-bind="allWrapperProps">
                            <div v-for="{ data: item } in virtualOptions"
                                :key="'all_' + props.getOptionKey(item)"
                                class="pb-2">
                                <page-select-item
                                    :label="props.getOptionLabel(item)"
                                    @toggle="toggle(item)"
                                    :selected="isSelected(item)"
                                    :disabled="item.disabled">
                                    <slot name="option"
                                        :item="item"
                                        :selected="isSelected(item)"></slot>
                                </page-select-item>
                            </div>
                        </div>
                    </div>
                </template>
                <div v-else
                    class="flex p-8 items-center justify-center flex-col gap-3">
                    <template v-if="props.loading">
                        <vx-icon-loading spin
                            class="w-6 h-6"></vx-icon-loading>
                        <span class="text-lg">Loading...</span>
                    </template>
                    <template v-else>
                        <font-awesome-icon widthAuto :icon="faSquareDashed"
                            class="text-2xl"></font-awesome-icon>
                        <span class="text-lg">No results found</span>
                    </template>
                </div>
            </div>

            <div v-else-if="tab === 'selected'" class="flex-1 min-h-0 flex flex-col">
                <template v-if="filteredSelected.length > 0">
                    <div
                        v-bind="selectedContainerProps"
                        class="flex-1 min-h-0 overflow-y-auto pt-6 px-6 lg:pt-10 lg:pb-8 lg:px-12">
                        <div v-if="props.showSelectAll && filteredSelected.length > 0"
                            class="flex justify-between items-center pl-3 sm:pl-4 pb-2">
                            <div @click="deselectAll()" role="button" tabindex="0" data-test="select-all-button"
                                @keydown.space.prevent="deselectAll()" @keydown.enter.prevent="deselectAll()"
                                class="flex items-center gap-2 cursor-pointer ml-px">
                                <vx-checkbox :modelValue="true" :tabindex="-1"></vx-checkbox>
                                <span class="text-sm whitespace-nowrap">Deselect All</span>
                            </div>
                            <slot name="select-all-right"/>
                        </div>
                        <div v-bind="selectedWrapperProps">
                            <div v-for="{ data: selectedItem } in virtualSelected"
                                :key="'selected_' + props.getOptionKey(selectedItem)"
                                class="pb-2">
                                <page-select-item
                                    :label="props.getOptionLabel(selectedItem)"
                                    @toggle="toggle(selectedItem)"
                                    :selected="true"
                                    :disabled="selectedItem.disabled">
                                    <slot name="option"
                                        :item="selectedItem"
                                        :selected="true"></slot>
                                </page-select-item>
                            </div>
                        </div>
                    </div>
                </template>
                <div v-else
                    class="flex p-8 items-center justify-center flex-col gap-3">
                    <font-awesome-icon widthAuto :icon="faSquareDashed"
                        class="text-2xl"></font-awesome-icon>
                    <span class="text-lg">
                        {{ search ? 'No results found' : 'Nothing selected' }}
                    </span>
                </div>
            </div>

        </div>
    </div>
</template>


<script setup>
import { faMagnifyingGlass, faSquareDashed, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useVirtualList } from '@vueuse/core';
import { VxCheckbox, VxIconLoading, VxInput, VxTab, VxTabs } from '@voxie/frontend-components';
import { computed, nextTick, ref, watch } from 'vue';
import PageSelectItem from '~/components/general/PageSelectItem.vue'

const selected = defineModel({
    type: Array,
    required: true,
});

const props = defineProps({
    options: {
        type: Array,
        required: true,
    },
    loading: {
        type: Boolean
    },
    getOptionKey: {
        type: Function,
        default(option) {
            if (typeof option !== 'object') {
                return option;
            }

            return option.id || option.value
        },
    },
    getOptionLabel: {
        type: Function,
        default(option) {
            if (typeof option !== 'object') {
                return option;
            }

            return option.name || option.label
        },
    },
    customSearch: {
        type: Function,
        default: null,
    },
    showSelectAll: {
        type: Boolean,
        default: true,
    },
});

const searchEl = ref()
const tab = ref('all')
const search = ref('')

const hasSearchText = computed(() => search.value.trim().length > 0)

const clearSearch = () => {
    search.value = ''
    nextTick().then(() => searchEl.value?.field?.focus?.())
}

watch(tab, () => {
    if (tab.value === 'selected') {
        search.value = '';
    }
    nextTick().then(() => searchEl.value?.field?.focus?.())
}, {
    immediate: true
});

watch(search, (search) => {
    emit('search', search);
})

const emit = defineEmits(['search', 'option:selected', 'option:deselected']);

const searchList = (list) => {
    const searchFor = search.value.trim()
    if (!searchFor.length) {
        return list;
    }

    return list.filter(option => {
        if (props.customSearch) {
            return props.customSearch(option, searchFor);
        }

        const searchForLower = searchFor.toLowerCase();
        const key = String(props.getOptionKey(option) || '').toLowerCase();
        const label = String(props.getOptionLabel(option) || '').toLowerCase();

        return key.includes(searchForLower) || label.includes(searchForLower);
    })
}

const filteredOptions = computed(() => searchList(props.options))
const filteredSelected = computed(() => searchList(selected.value))

const virtualListOptions = { itemHeight: 64, overscan: 10 };
const { list: virtualOptions, containerProps: allContainerProps, wrapperProps: allWrapperProps } = useVirtualList(filteredOptions, virtualListOptions);
const { list: virtualSelected, containerProps: selectedContainerProps, wrapperProps: selectedWrapperProps } = useVirtualList(filteredSelected, virtualListOptions);

watch(search, () => {
    if (allContainerProps.ref.value) {
        allContainerProps.ref.value.scrollTop = 0;
    }
    if (selectedContainerProps.ref.value) {
        selectedContainerProps.ref.value.scrollTop = 0;
    }
});

const selectedKeys = computed(() => new Set(selected.value.map(o => props.getOptionKey(o))))

const isSelected = (optionToCheck) => {
    return selectedKeys.value.has(props.getOptionKey(optionToCheck))
}

const toggle = (optionToToggle) => {
    const key = props.getOptionKey(optionToToggle);
    if (selectedKeys.value.has(key)) {
        selected.value = selected.value.filter(option => props.getOptionKey(option) !== key)
        emit('option:deselected', optionToToggle)
    } else {
        selected.value = [...selected.value, optionToToggle];
        emit('option:selected', optionToToggle)
    }
}

const selectableOptions = computed(() => filteredOptions.value.filter(option => !option.disabled))

const selectAll = computed(() => {
    if (selectableOptions.value.length === 0) {
        return false;
    }
    const keys = selectedKeys.value;
    return selectableOptions.value.every(option => keys.has(props.getOptionKey(option)))
})

const deselectAll = () => {
    selected.value.forEach(selectedOption => {
        emit('option:deselected', selectedOption);
    })
    selected.value = []
}

const toggleAll = () => {
    if (selectAll.value) {
        deselectAll();
    } else {
        const keys = selectedKeys.value;
        const newSelections = selectableOptions.value.filter(option => !keys.has(props.getOptionKey(option)));
        newSelections.forEach(option => {
            emit('option:selected', option)
        })
        selected.value = [...selected.value, ...newSelections];
    }
}

const handleEnter = () => {
    if (!search.value.length) {
        return;
    }

    if (props.loading) {
        return;
    }

    const firstOption = filteredOptions.value[0];

    if (firstOption && !firstOption.disabled) {
        toggle(firstOption);
    }
};
</script>
